import { notification, Popconfirm } from "antd";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContext";
import DashboardIcon from "../../assets/dashboard.svg";
import DevicesIcon from "../../assets/devices.svg";
import HistoryIcon from "../../assets/history.svg";
import LocationIcon from "../../assets/locations.svg";
import LogoutIcon from "../../assets/logout_icon.svg";
import SimIcon from "../../assets/sim.svg";
import TagsIcon from "../../assets/tags.svg";
import UsersIcon from "../../assets/users.svg";
import WorkOrderIcon from "../../assets/work_order.svg";
import ShiftIcon from "../../assets/shift.svg";
import tr from "../../i18n";
import { hasAccess, hasRoles, ROLES } from "../../utils/AccessLevels";
import "./SideBar.css";

const menuItems = [
  { path: "/control-center/gardens", index: 1 },
  { path: "/control-center/users/devices", index: 2 },
  { path: "/control-center/dashboard", index: 3 },
  { path: "/control-center/users/user-access", index: 4 },
  { path: "/control-center/users/tags", index: 5 },
  { path: "/control-center/work-orders", index: 6 },
  { path: "/control-center/shifts", index: 7 },
  { path: "/control-center/users/audit", index: 8 },
  { path: "/control-center/connection-dashboard", index: 9 },
];

const SideBar = ({ onCloseSidebar }) => {
  const navigate = useNavigate();
  const [selectedMenuIndex, setSelectedMenuOIndex] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const { role } = useContext(AppContext);
  const { setToken } = useContext(AppContext);
  const location = useLocation();
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const hostname = window.location.hostname
  const hostParts = hostname.split('.');
  const queryClient = useQueryClient();
  const isGrungardTenant =
    hostParts.length >= 3 &&
    hostParts[0] === 'grungard' &&
    (hostParts[1] === 'grungard' || hostParts[1] === 'akillisula');

  const SHIFT_ALLOWED_HOSTNAMES = ['bilisimvadisi.akillisula.com',
    'bilisimvadisi.grungard.ai',
    'darica.akillisula.com',
    'darica.grungard.ai'
  ]

  const SHIFT_EXCLUSIVE_HOSTNAMES = [
    'daricazabita.akillisula.com',
    'daricazabita.grungard.ai'
  ]

  useEffect(() => {
    try {
      const userSettings = localStorage.getItem("settings");
      if (userSettings) {
        const settings = JSON.parse(userSettings);
        setTenantLogoUrl(settings.logoUrl || null);
      }
    } catch (error) {
      console.error(error);
    }
  }, [])


  useEffect(() => {
    const token = localStorage.getItem("token");
    setToken(token);
    const userObj = localStorage.getItem("user");
    if (window.innerWidth < 768) {
      console.log(`Toggling sidebar`);
      onCloseSidebar();
    }
    setUser(JSON.parse(userObj));

    if (!token) {
      navigate("/login");
    }
  }, [navigate, onCloseSidebar, setToken]);

  useEffect(() => {
    const currentItem = menuItems.find((item) => location.pathname.includes(item.path));
    if (currentItem) {
      setSelectedMenuOIndex(currentItem.index);
    }
  }, [location.pathname]);

  const logoutUser = useMutation(
    () => {
      return axios.post(`${process.env.REACT_APP_GARDENER_API_END_POINT}/auth/logout`);
    },
    {
      onSuccess: () => {
        notification.success({
          message: tr("User logged out successfully"),
        });
      },
      onError: (error) => {
        notification.error({
          message: tr("Something went wrong while logging out user"),
        });
      },
    }
  );

  const onLogout = async () => {
    try {
      await logoutUser.mutateAsync();
    } catch (error) {
    } finally {
      localStorage.removeItem("token");
      setToken(null);
      localStorage.removeItem("settings");
      localStorage.removeItem("user");
      queryClient.removeQueries(["Gardens"]);
      queryClient.removeQueries("Devices");
      queryClient.removeQueries(["GetTags"]);
      navigate("/login");
    }
  };

  return (
    <ul className="text-white text-sm15" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {!isGrungardTenant && !SHIFT_EXCLUSIVE_HOSTNAMES.includes(hostname) && (hasAccess(ROLES.TECHNICAL_ADMIN, role) || hasRoles(ROLES.REGIONAL_MANAGER, role)) && (
        <li
          className={`${selectedMenuIndex === 3 ? "sidebar-item-active" : ""} sidebar-item`}
          onClick={() => {
            navigate("/control-center/dashboard");
            if (typeof onCloseSidebar === "function") onCloseSidebar();
          }}
        >
          <img alt="" src={DashboardIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3"}>{tr("Dashboard")}</span>
        </li>
      )}

      <li
        className={`${selectedMenuIndex === 1 ? " sidebar-item-active " : ""} sidebar-item`}
        onClick={() => {
          navigate("/control-center/gardens");
          if (typeof onCloseSidebar === "function") onCloseSidebar();
        }}
      >
        <img alt="" src={LocationIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
        <span className={"px-3"}>{tr("Gardens")}</span>
      </li>

      {(!SHIFT_EXCLUSIVE_HOSTNAMES.includes(hostname)) && (
        <li
          className={`${selectedMenuIndex === 2 ? " sidebar-item-active " : ""} sidebar-item`}
          onClick={() => {
            navigate("/control-center/users/devices");
            if (typeof onCloseSidebar === "function") onCloseSidebar();
          }}
        >
          <img alt="" src={DevicesIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3"}>{tr("Devices")}</span>
        </li>
      )
      }

      {hasAccess(ROLES.FIELD_MANAGER, role) && (
        <li
          className={`${selectedMenuIndex === 4 ? "sidebar-item-active " : ""} sidebar-item`}
          onClick={() => {
            navigate("/control-center/users/user-access");
            if (typeof onCloseSidebar === "function") onCloseSidebar();
          }}
        >
          <img alt="" src={UsersIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3"}>{tr("Users")}</span>
        </li>
      )}

      {hasAccess(ROLES.FIELD_MANAGER, role) &&
        <li
          className={`${selectedMenuIndex === 5 ? "sidebar-item-active " : ""} sidebar-item`}
          onClick={() => {
            navigate("/control-center/users/tags");
            if (typeof onCloseSidebar === "function") onCloseSidebar();
          }}
        >
          <img alt="" src={TagsIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3"}>{tr("Tags")}</span>
        </li>
      }


      {(!SHIFT_EXCLUSIVE_HOSTNAMES.includes(hostname)) && hasAccess(ROLES.REGIONAL_MANAGER, role) && (
        <li
          className={`${selectedMenuIndex === 6 ? "sidebar-item-active " : ""}  sidebar-item`}
          onClick={() => {
            navigate("/control-center/work-orders");
            if (typeof onCloseSidebar === "function") onCloseSidebar();
          }}
        >
          <img alt="" src={WorkOrderIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3"}>{tr("Work Orders")}</span>
        </li>
      )}

      {
        (hostname && (SHIFT_ALLOWED_HOSTNAMES.includes(hostname) || SHIFT_EXCLUSIVE_HOSTNAMES.includes(hostname))) && (
          <li className={`${selectedMenuIndex == 7 ? 'sidebar-item-active ' : ''}  sidebar-item`}
            onClick={() => {
              navigate('/control-center/shifts')
              if (typeof onCloseSidebar === "function") onCloseSidebar();
            }}>
            <img alt="" src={ShiftIcon} className='inline-block w-6 h-6 mr-2 ml-2 text-white' />
            <span className={`px-3`}>{tr("Shift Report")}</span>
          </li>
        )
      }

      {
        hasAccess(ROLES.TECHNICAL_ADMIN, role) && (
          <li
            className={`${selectedMenuIndex === 8 ? "sidebar-item-active " : ""}  sidebar-item`}
            onClick={() => {
              navigate("/control-center/users/audit");
              if (typeof onCloseSidebar === "function") onCloseSidebar();
            }}
          >
            <img alt="" src={HistoryIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
            <span className={"px-3"}>{tr("History")}</span>
          </li>
        )
      }

      {
        hasAccess(ROLES.SUPER_ADMIN, role) && (
          <li
            className={`${selectedMenuIndex === 9 ? "sidebar-item-active " : ""}  sidebar-item`}
            onClick={() => {
              navigate("/control-center/connection-dashboard");
              if (typeof onCloseSidebar === "function") onCloseSidebar();
            }}
          >
            <img alt="" src={SimIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
            <span className={"px-3"}>{tr("Connection Dashboard")}</span>
          </li>
        )
      }

      <li id={"drawer-spacer"} style={{ alignItems: "flex-end", display: "flex", flexGrow: 1 }}>
        <img src={tenantLogoUrl} alt="Tenant Logo" style={{ borderRadius: 12, margin: "auto 0 auto 25px", width: 170 }} />
      </li>

      <Popconfirm
        title={
          <div>
            <div className=" text-red-500"> {tr("Logout Prompt")}</div>
          </div>
        }
        okText={tr("Yes")}
        cancelText={tr("No")}
        onCancel={() => { }}
        overlayClassName=" rounded-lg "
        onConfirm={onLogout}
        overlayStyle={{ zIndex: 100000 }}
      >
        <li className="sidebar-item">
          <img alt="" src={LogoutIcon} className="inline-block w-6 h-6 mr-2 ml-2 text-white" />
          <span className={"px-3 text-white"}>{tr("Logout")}</span>
        </li>
      </Popconfirm>
    </ul >
  );
};

export default SideBar;
